import React, { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../context/ApiContext";
import Button from "./ui/Button";
import ProgressBar from "./ui/ProgressBar";
import SegmentPreview from "./SegmentPreview";
import "./SegmentPreview.css";

/**
 * Ingestion d'un document, pilotée par le navigateur.
 *
 * POURQUOI CE SENS
 * ----------------
 * L'API lançait auparavant un script détaché et rendait la main aussitôt. Le
 * navigateur ne pouvait alors que DEVINER l'avancement, en interrogeant en
 * boucle un état stocké côté serveur. De là toute une mécanique — identifiant
 * de corrélation, stockage partagé entre pods, endpoint de suivi, sondage,
 * journaux à consulter en cas de doute — dont chaque pièce n'existait que pour
 * compenser le fait qu'on ne savait pas ce qui se passait, et dont chacune
 * pouvait tomber en panne séparément.
 *
 * Ici, le navigateur découpe lui-même : il récupère les segments (la
 * prévisualisation est synchrone et rapide), puis les envoie par lots. Chaque
 * appel répond ce qu'il a RÉELLEMENT écrit. La barre compte, elle n'estime pas.
 *
 * Contrepartie assumée : fermer l'onglet interrompt l'indexation. Les
 * identifiants étant déterministes, relancer reprend sans créer de doublon —
 * on ne perd que du temps. D'où l'avertissement affiché pendant le traitement
 * et le garde-fou sur le rechargement de page.
 */

/** Taille d'un lot. Assez grand pour limiter les allers-retours, assez petit
 *  pour que la barre avance visiblement et qu'aucun appel ne traîne. */
const TAILLE_LOT = 20;

export default function DocumentIndexer({ accept, fileLabel, storagePrefix }) {
    const { apiFetch } = useApi();

    const [file, setFile] = useState(null);
    const [collection, setCollection] = useState(localStorage.getItem(`${storagePrefix}_collection`) || "");
    const [language, setLanguage] = useState(localStorage.getItem(`${storagePrefix}_language`) || "");
    const [support, setSupport] = useState(localStorage.getItem(`${storagePrefix}_support`) || "brochure");
    const [country, setCountry] = useState(localStorage.getItem(`${storagePrefix}_country`) || "");
    const [campaign, setCampaign] = useState(
        localStorage.getItem(`${storagePrefix}_campaign`) || String(new Date().getFullYear())
    );

    const [collections, setCollections] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [supports, setSupports] = useState([]);

    const [segments, setSegments] = useState(null);
    const [engine, setEngine] = useState("");
    const [doublons, setDoublons] = useState(0);
    const [analyse, setAnalyse] = useState(false);
    const [error, setError] = useState("");

    const [progress, setProgress] = useState({ written: 0, failed: 0, total: 0 });
    const [phase, setPhase] = useState("idle"); // idle | running | done | error
    const [message, setMessage] = useState("");
    const interrompu = useRef(false);

    useEffect(() => {
        apiFetch("/collections").then(r => r.json()).then(j => setCollections(j.data || []));
        apiFetch("/languages").then(r => r.json()).then(j => setLanguages(j.data || []));
        apiFetch("/supports").then(r => r.json()).then(j => setSupports(j.data || []));
    }, [apiFetch]);

    /* Garde-fou navigateur : tant qu'une indexation tourne, un rechargement ou
       une fermeture d'onglet la coupe net. On demande confirmation. */
    useEffect(() => {
        if (phase !== "running") {
            return undefined;
        }
        const garde = (e) => {
            e.preventDefault();
            e.returnValue = "";           // exigé par les navigateurs pour afficher la boîte
            return "";
        };
        window.addEventListener("beforeunload", garde);
        return () => window.removeEventListener("beforeunload", garde);
    }, [phase]);

    const remember = (cle, setter) => (e) => {
        const value = e.target.value;
        setter(value);
        localStorage.setItem(`${storagePrefix}_${cle}`, value);
    };

    const champsManquants = () => {
        if (!file) return "Sélectionnez un document.";
        if (collection === "") return "La collection est obligatoire.";
        if (language === "") return "La langue est obligatoire.";
        if (support === "") return "Le support est obligatoire.";
        return "";
    };

    /** Découpage à blanc : aucune écriture, réponse immédiate. */
    const analyser = useCallback(async () => {
        const manque = champsManquants();
        if (manque) {
            setError(manque);
            return null;
        }

        setError("");
        setAnalyse(true);
        setSegments(null);
        setPhase("idle");
        setProgress({ written: 0, failed: 0, total: 0 });

        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("language", language);

            const json = await apiFetch("/vectors/preview", { method: "POST", body: fd }).then(r => r.json());
            if (!json.success) {
                setError(json.error || "Le découpage a échoué.");
                return null;
            }
            setEngine(json.engine || "");
            setDoublons(json.duplicates || 0);
            setSegments(json.segments || []);
            return json.segments || [];
        } catch (err) {
            setError("Impossible de contacter l'API : " + err.message);
            return null;
        } finally {
            setAnalyse(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiFetch, file, collection, language, support]);

    /** Envoi par lots. C'est ici que la progression devient réelle. */
    const indexer = async (brut) => {
        // Filet de sécurité. Le dédoublonnage réel a lieu à la prévisualisation,
        // côté serveur, avec la fonction de hachage exacte de l'indexation : c'est
        // ce qui garantit que le nombre annoncé sur le bouton est celui que la
        // barre atteindra. Ce Set ne fait plus que se prémunir d'un appelant
        // futur qui passerait une liste non filtrée.
        const liste = Array.from(new Set((brut || []).map(s => s.trim()).filter(Boolean)));

        if (liste.length === 0) {
            setError("Aucun segment à indexer.");
            return;
        }

        interrompu.current = false;
        setError("");
        setMessage("");
        setPhase("running");
        setProgress({ written: 0, failed: 0, total: liste.length });

        let ecrits = 0;
        let echecs = 0;

        for (let i = 0; i < liste.length; i += TAILLE_LOT) {
            if (interrompu.current) {
                setPhase("error");
                setMessage(`Interrompu : ${ecrits} segment(s) déjà indexés sont conservés.`);
                return;
            }

            const lot = liste.slice(i, i + TAILLE_LOT);
            let json;
            try {
                json = await apiFetch("/vectors/segments", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ segments: lot, collection, language, support, country, campaign }),
                }).then(r => r.json());
            } catch (err) {
                setPhase("error");
                setMessage(`Réseau interrompu après ${ecrits} segment(s) : ${err.message}`);
                return;
            }

            // Un lot entièrement en échec signale une cause générale (clé OpenAI,
            // Qdrant injoignable) : continuer ne ferait que répéter l'erreur.
            if (!json.success) {
                setPhase("error");
                setMessage(`${json.error || "Échec du lot."} — ${ecrits} segment(s) indexés avant l'arrêt.`);
                return;
            }

            ecrits += json.written || 0;
            echecs += json.failed || 0;
            setProgress({ written: ecrits, failed: echecs, total: liste.length });
        }

        setPhase("done");
        setMessage(echecs > 0
            ? `${ecrits} segment(s) indexés, ${echecs} en échec.`
            : `${ecrits} segment(s) indexés.`);
    };

    /* Sans prévisualisation : on analyse puis on indexe tout, d'une traite. */
    const toutIndexer = async (e) => {
        e.preventDefault();
        const liste = await analyser();
        if (liste) {
            await indexer(liste);
        }
    };

    const enCours = phase === "running";

    return (
        <form onSubmit={toutIndexer} className="form-grid">
            <div className="field field--wide">
                <label htmlFor={`${storagePrefix}-file`}>{fileLabel}</label>
                <input
                    id={`${storagePrefix}-file`}
                    type="file"
                    accept={accept}
                    disabled={enCours}
                    onChange={e => { setFile(e.target.files[0] || null); setSegments(null); setPhase("idle"); }}
                />
            </div>

            <div className="field">
                <label htmlFor={`${storagePrefix}-collection`}>Collection</label>
                <select id={`${storagePrefix}-collection`} value={collection} disabled={enCours}
                        onChange={remember("collection", setCollection)}>
                    <option value="">—</option>
                    {collections.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            <div className="field">
                <label htmlFor={`${storagePrefix}-language`}>Langue</label>
                <select id={`${storagePrefix}-language`} value={language} disabled={enCours}
                        onChange={remember("language", setLanguage)}>
                    <option value="">—</option>
                    {languages.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
            </div>

            <div className="field">
                <label htmlFor={`${storagePrefix}-support`}>Support</label>
                <select id={`${storagePrefix}-support`} value={support} disabled={enCours}
                        onChange={remember("support", setSupport)}>
                    <option value="">—</option>
                    {supports.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            <div className="field">
                <label htmlFor={`${storagePrefix}-country`}>Pays</label>
                {/* Texte libre, mais avec les slugs qui comptent en suggestion :
                    USA, Canada et France ont des brochures propres, distinctes de
                    l'international. La liste ne restreint pas la saisie. */}
                <input id={`${storagePrefix}-country`} type="text" placeholder="ex. usa_en"
                       list="pays-slugs" value={country} disabled={enCours}
                       onChange={remember("country", setCountry)} />
                <datalist id="pays-slugs">
                    <option value="usa_en" />
                    <option value="usa_esal" />
                    <option value="canada_en" />
                    <option value="canada_fr" />
                    <option value="france_fr" />
                </datalist>
                <span className="field__hint">
                    Facultatif : à laisser vide si le document vaut pour toute la langue.
                </span>
            </div>

            <div className="field">
                <label htmlFor={`${storagePrefix}-campaign`}>Campagne</label>
                <input id={`${storagePrefix}-campaign`} type="text" placeholder="2026"
                       value={campaign} disabled={enCours} onChange={remember("campaign", setCampaign)} />
                <span className="field__hint">Permet de remplacer une année sans toucher aux autres.</span>
            </div>

            {error && <p className="notice notice--error field--wide">{error}</p>}

            {segments !== null && (
                <div className="field--wide">
                    <SegmentPreview
                        segments={segments}
                        engine={engine}
                        duplicates={doublons}
                        disabled={enCours}
                        onConfirm={indexer}
                    />
                </div>
            )}

            <ProgressBar
                written={progress.written}
                failed={progress.failed}
                total={progress.total}
                state={phase}
                message={message}
                onAbort={() => { interrompu.current = true; }}
            />

            <div className="form-actions">
                <Button type="button" onClick={analyser} disabled={analyse || enCours}>
                    {analyse ? "Analyse…" : "Analyser le document"}
                </Button>
                <Button variant="secondary" type="submit" disabled={analyse || enCours}>
                    Indexer sans prévisualiser
                </Button>
                <span className="field__hint">
                    La prévisualisation est recommandée : un document de maquette comporte
                    toujours quelques blocs qui n'ont rien à faire en mémoire.
                </span>
            </div>
        </form>
    );
}
